import { useCallback, useState, useSyncExternalStore } from 'react';
import { isFunction } from '@philips-internal/oswald-ui-utils';
const SSOT = 'ssot';
/**
 * Hook that allows component state properties to be shared with the component API through the Single Source of Truth.
 *
 * @param options - Hook options.
 * @param options.initialValue - The initial value of the shared state property;
 * @param options.name - The name of the state property being shared;
 * @param options.props - The entire properties object of the component, containing the SSOT.
 */
function useSharedState(options) {
    const { initialValue, name, props } = options;
    const [useStateValue, setUseStateValue] = useState(initialValue);
    const ssot = Object.prototype.hasOwnProperty.call(props, SSOT)
        // @ts-ignore: Ignoring because the SSOT property is not explicility declared in the props object
        ? props[SSOT]
        : null;
    if (!ssot) {
        // @ts-ignore: Ignoring because our <T> type is the most correct one.
        return [useStateValue, setUseStateValue];
    }
    // Create a callback so the subscribe function remains the same between renders.
    const subscribeToState = useCallback((onChange) => {
        return ssot.subscribeToState(onChange);
    }, []);
    const value = useSyncExternalStore(subscribeToState, () => {
        return ssot.state[name];
    });
    if (initialValue) {
        ssot.apply({ [name]: initialValue });
    }
    const updateValue = (updatedValue) => {
        const previousValue = ssot.state[name];
        let nextValue = updatedValue;
        // @ts-ignore: Ignoring because typescript can't correctly infer.
        if (isFunction(updatedValue)) {
            nextValue = updatedValue(previousValue);
        }
        ssot.applyAndNotify({ [name]: nextValue });
    };
    return [value, updateValue];
}
export { useSharedState, };
//# sourceMappingURL=use-shared-state.js.map